import { useEffect, useRef } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import * as api from "../api";
import type { SolutieRand } from "../nav/api";
import type { useT } from "../i18n";
import { lipesteFelia } from "./memorie";
import { northUp, type CanonicalDoc } from "./canonical";
import type { Selection } from "./inspect";
import type { Tinta } from "./Vizualizator";

/** Cât se așteaptă, după primul desen, înainte de a cere restul nivelurilor.
 *  Cât să apuce pânza să se așeze — traducerea documentului și prima desenare
 *  — fără ca omul să ajungă să apese altă cotă înaintea lui. */
const INTARZIERE_REST_MS = 1200;

/** Cât din document e pe ecran — v. `felia` în `Vizualizator`. */
export type Felia = { complet: boolean; nivel: number | null };

/** DOCUMENTUL SOLUȚIEI DESCHISE: deschiderea (felia de start), restul
 *  nivelurilor în fundal și recitirea după o revizie (`reciteste`).
 *
 *  Starea (`doc`, `felia`, `eroare`, `reciteste`) rămâne declarată în
 *  `Vizualizator`, unde era: între ea și efectele de aici stau alte hook-uri
 *  care o citesc, iar ordinea hook-urilor nu se schimbă. Efectul de deschidere
 *  golește și ce țin celelalte părți ale vizualizatorului (camera, arhitectura
 *  adusă) prin cele două funcții primite, chemate în același loc din efect. */
export function useDocumentSolutie({
  projectId, solution, doc, setDoc, felia, setFelia, setEroare, reciteste,
  setReciteste, tintaRef, recompMoale, fatadaMoale, setSelectie, setNivelAles,
  setCorpAles, setVedeSituatia, setDocArh, setDocSitBrut, setArhSeIncarca,
  mostenesteCamera, uitaArhitecturaAdusa, t, docFix = null,
}: {
  projectId: string;
  solution: SolutieRand;
  doc: CanonicalDoc | null;
  setDoc: Dispatch<SetStateAction<CanonicalDoc | null>>;
  felia: Felia;
  setFelia: Dispatch<SetStateAction<Felia>>;
  setEroare: Dispatch<SetStateAction<string>>;
  reciteste: number;
  setReciteste: Dispatch<SetStateAction<number>>;
  tintaRef: MutableRefObject<Tinta | null>;
  recompMoale: MutableRefObject<{ faza: "job" | "gata"; la: number } | null>;
  fatadaMoale: MutableRefObject<number | null>;
  setSelectie: Dispatch<SetStateAction<Selection | null>>;
  setNivelAles: Dispatch<SetStateAction<number | null>>;
  setCorpAles: Dispatch<SetStateAction<string | null>>;
  setVedeSituatia: Dispatch<SetStateAction<boolean>>;
  setDocArh: (d: CanonicalDoc | null) => void;
  setDocSitBrut: Dispatch<SetStateAction<CanonicalDoc | null>>;
  setArhSeIncarca: Dispatch<SetStateAction<boolean>>;
  /** Reține camera documentului de dinainte (v. `usePanZoom`). */
  mostenesteCamera: () => void;
  /** Uită ce arhitectură s-a adus (v. `useArhitecturaInlantuita`). */
  uitaArhitecturaAdusa: () => void;
  t: ReturnType<typeof useT>;
  /** Documentul dat de ecran în locul celui al soluției: se pune pe ecran
   *  întreg, fără cerere (exemplul Instalațiilor, care nu stă pe server). */
  docFix?: CanonicalDoc | null;
}) {
  const solutiaDinainte = useRef<SolutieRand | null>(null);

  useEffect(() => {
    // O VARIANTĂ DE RECOMPARTIMENTARE TOCMAI SCRISĂ schimbă doar `updated_at`: aceeași
    // soluție, aceeași stare. Se recitește peste ce e pe ecran, fără să golească nimic
    // (v. `recompMoale`).
    {
      const dinainte = solutiaDinainte.current;
      const moale = recompMoale.current;
      if (moale && Date.now() - moale.la < 120_000 && dinainte != null
          && dinainte.id === solution.id && dinainte.status === solution.status) {
        solutiaDinainte.current = solution;
        if (moale.faza === "gata") recompMoale.current = null;
        else setReciteste((n) => n + 1);
        return;
      }
      // …și una a PANOULUI DE FAȚADĂ: documentul se recitește (sau e deja pe
      // ecran) prin `laSchimbare`, nu de la zero
      const fat = fatadaMoale.current;
      if (fat != null && Date.now() - fat < 120_000 && dinainte != null
          && dinainte.id === solution.id && dinainte.status === solution.status) {
        solutiaDinainte.current = solution;
        fatadaMoale.current = null;
        return;
      }
    }
    // O REVIZIE NOUĂ A ACELEIAȘI SOLUȚII, de oricine ar veni, se recitește la
    // fel: peste ce e pe ecran. Jobul arhitecturii își rescrie rândul la pasul
    // 1/2 și iar la sfârșit (subsolul, parcarea), o parcare refăcută la fel,
    // agentul de fațadă scrie documentul și apoi de două ori rezumatul, pasul
    // 2 al situației reface circulația după arhitectură. Fiecare schimba
    // `updated_at`, iar ramura de dedesubt golea documentul — macheta cădea și
    // se ridica din nou, cu tot cu ce alesese omul («mi se dă refresh la
    // scenă», Cătălin, 13.10.2026). Numai cât rândul are un desen: o soluție
    // repornită (`queued`) sau picată se deschide de la capăt.
    {
      const dinainte = solutiaDinainte.current;
      const cuDesen = (s: string | undefined) =>
        s === "running" || s === "ready" || s === "nonconvergent";
      if (dinainte != null && dinainte.id === solution.id
          && cuDesen(dinainte.status) && cuDesen(solution.status)) {
        solutiaDinainte.current = solution;
        setReciteste((n) => n + 1);
        return;
      }
    }
    // CAMERA SE MOȘTENEȘTE de la documentul de dinainte — v. `mostenesteCamera`
    // în `usePanZoom`.
    mostenesteCamera();
    setDoc(null);
    setEroare("");
    setSelectie(null);
    // COTA RĂMÂNE între o soluție și încercările ei: cât se generează subsolul,
    // Studio arată parcarea de dinaintea rampelor și apoi soluția gata, iar
    // omul care se uita la -1 trebuie să rămână la -1.
    const dinainte = solutiaDinainte.current;
    solutiaDinainte.current = solution;
    const inrudite = dinainte != null && (dinainte.id === solution.id
      || dinainte.id === solution.parent_solution_id
      || dinainte.parent_solution_id === solution.id);
    if (!inrudite) setNivelAles(null);
    setCorpAles(null);
    setVedeSituatia(solution.discipline === "site_plan");
    setDocArh(null);
    setDocSitBrut(null);
    setArhSeIncarca(false);
    uitaArhitecturaAdusa();

    // FĂRĂ MEMORIE ÎNTRE ECRANE. Se ținea documentul adus, ca o comutare
    // situație → arhitectură → situație să nu-l mai ceară o dată. Numai că o
    // soluție REFĂCUTĂ păstrează id-ul și starea, deci cheia rămânea aceeași
    // și se punea pe ecran copia veche — o machetă compusă din jumătate de
    // ieri și jumătate de azi, imposibil de citit ca atare. Se cere de fiecare
    // dată; costă felia de start (222 KB), nu cei 12 MB.
    let renunta = false;
    // FELIA DE START, nu documentul întreg: contextul, planșeele și cota pe
    // care se deschide desenul. Pe un ansamblu real înseamnă 222 KB în loc de
    // 1 443 și 1,89 MB de parsat în loc de 12,41 (v. `services/felii.py`);
    // restul vine singur, în fundal, prin efectul de dedesubt.
    //
    // Cota o alege serverul după aceeași regulă ca `nivelImplicit` — afară de
    // cazul în care un link cere un element anume, și atunci se cere cota LUI:
    // altfel primul desen ar fi al parterului, iar ținta abia al doilea.
    if (docFix) {
      setDoc(northUp(docFix));
      setFelia({ complet: true, nivel: null });
      return;
    }
    api.solutionDocument(projectId, solution.id,
                         { parte: "start", nivel: tintaRef.current?.level })
      // NORDUL SUS, O DATĂ, LA CITIRE. Documentul e un model GEOGRAFIC —
      // nordul e o direcție de busolă, nu una de ecran —, iar SVG-ul desenează
      // y ÎN JOS. `northUp` întoarce documentul ÎNTREG în cadrul desenului și e
      // singurul loc unde se citește `crs`: ce corecție e nevoie (sfert de tură
      // pe grilele northing-first, oglindire pe y în rest) o alege el, fiindcă
      // cele două se exclud. Vezi nota lui.
      //
      // De aici încolo totul — traducerea, randarea, țintirea, adnotările,
      // macheta 3D, exportul PDF — lucrează într-un singur spațiu, și NIMIC nu
      // mai are voie să întrebe pe ce CRS e documentul ca să întoarcă ceva.
      // Regula asta e defectul, scris ca invariant: cât timp orientarea s-a
      // aplicat doar stratului de SITUAȚIE, arhitectura generată a aceluiași
      // proiect s-a desenat cu nordul la dreapta, un sfert de tură față de
      // planul lui de situație.
      //
      // Completarea trece prin ACEEAȘI transformare, la lipire — v.
      // `memorie.lipesteFelia`.
      .then((d) => {
        if (renunta) return;
        const doc = northUp(d as unknown as CanonicalDoc);
        const felie = { complet: d.felie?.complet !== false,
                        nivel: d.felie?.nivel ?? null };
        setDoc(doc);
        setFelia(felie);
      })
      .catch(() => { if (!renunta) setEroare(t("viz.doc_necitit")); });
    return () => { renunta = true; };
    // ȘI LA SCHIMBAREA STĂRII, nu doar a soluției: o arhitectură deschisă în
    // lucru are documentul etajelor; când trece pe `ready`, același rând are
    // și parcarea, iar desenul trebuie recitit.
  }, [projectId, solution.id, solution.status, solution.updated_at, docFix]);

  // RESTUL NIVELURILOR, după ce primul desen e pe ecran.
  //
  // Nu la cerere, când omul apasă altă cotă: atunci ar aștepta o secundă la
  // fiecare buton, adică ar plăti de mai multe ori exact ce felia scutise o
  // dată. Se aduce singur, cu o pauză cât să nu ia banda desenului care tocmai
  // se așază — același tipar ca preîncărcarea planului 2D din Studioul
  // structurii — și se lipește peste ce e deja pe ecran. Din clipa aia
  // documentul e întreg: cotele, inventarul și proiecția nivelului de dedesubt
  // răspund fără nicio altă cerere.
  useEffect(() => {
    if (!doc || felia.complet) return;
    let renunta = false;
    // PAUZA E PENTRU PRIMUL DESEN, deci nu se ține când desenul cerut nu e cel
    // pe care îl avem: un link către un element de la etajul 3, deschis peste
    // o felie de parter ținută în memorie, ar sta o secundă pe o pânză goală —
    // cotele există (planșeele vin toate cu felia de start), dar planșeul
    // căutat n-are încă nimic pe el.
    const tintaAltundeva = tintaRef.current != null
      && tintaRef.current.level !== felia.nivel;
    const ceas = setTimeout(() => {
      api.solutionDocument(projectId, solution.id,
                           { parte: "rest", nivel: felia.nivel })
        .then((rest) => {
          if (renunta) return;
          const intreg = lipesteFelia(doc, rest);
          setDoc(intreg);
          setFelia({ complet: true, nivel: felia.nivel });
        })
        // Restul lipsă nu e o eroare de arătat: desenul de pe ecran e întreg
        // pentru cota lui. Rămâne felia, și se reîncearcă la următoarea vizită.
        .catch(() => undefined);
    }, tintaAltundeva ? 0 : INTARZIERE_REST_MS);
    return () => { renunta = true; clearTimeout(ceas); };
  }, [projectId, solution.id, solution.status, solution.updated_at, doc, felia]);

  // RECITIREA după o scriere din editor, sau după o revizie nouă a soluției
  // venită din registru: documentul întreg, pus peste cel de pe ecran fără să-l
  // golească întâi (v. `reciteste`).
  /** Soluția de pe ecran ACUM — recitirea de mai jos nu-și pune documentul peste
   *  altă soluție deschisă între timp (dependențele ei sunt numai contorul). */
  const solutiaDeAcum = useRef(solution.id);
  solutiaDeAcum.current = solution.id;
  useEffect(() => {
    if (!reciteste) return;
    let renunta = false;
    const ceruta = solution.id;
    api.solutionDocument(projectId, ceruta)
      .then((d) => {
        if (renunta || solutiaDeAcum.current !== ceruta) return;
        setDoc(northUp(d as unknown as CanonicalDoc));
        setFelia((f) => ({ complet: true, nivel: f.nivel }));
        // o arhitectură deschisă înainte de pasul 1/2 n-avea încă document
        setEroare("");
      })
      .catch(() => { if (!renunta) setEroare(t("viz.doc_necitit")); });
    return () => { renunta = true; };
  }, [reciteste]);
}
